import type { AdminPage, StaffRole, StaffUser } from '@neighbourhood-munchies/shared-types';
import type { Session, User } from '@supabase/supabase-js';
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { apiClient } from '../services/api';
import { supabase } from '../services/supabase';
import { clearAuthData, isSessionExpired } from '../utils/sessionManager';

/**
 * Returns the default landing route for a given staff role.
 * Used to redirect users to an accessible page after login.
 */
export function getDefaultRoute(role: StaffRole | undefined): string {
  switch (role) {
    case 'dispatch':
      return '/live-orders';
    case 'accounts':
    case 'operations':
    case 'admin':
    case 'super_admin':
    default:
      return '/dashboard';
  }
}

/**
 * Role-based permission matrix
 * Defines which pages each staff role can access
 */
const ROLE_PERMISSIONS: Record<StaffRole, AdminPage[]> = {
  dispatch: ['live-orders', 'order-management', 'customer-management'],
  accounts: ['dashboard', 'analytics', 'order-management', 'customer-management'],
  operations: ['order-management', 'live-orders', 'dashboard', 'analytics'],
  admin: [
    'dashboard',
    'analytics',
    'live-orders',
    'order-management',
    'restaurants',
    'menu-items',
    'zones',
    'customizations',
    'advertisements',
  ],
  super_admin: [
    'dashboard',
    'analytics',
    'live-orders',
    'order-management',
    'restaurants',
    'menu-items',
    'zones',
    'customizations',
    'advertisements',
    'customer-management',
    'staff-management',
    'system-settings',
  ],
};

interface AuthContextType {
  user: User | null;
  staffUser: StaffUser | null;
  session: Session | null;
  loading: boolean;
  signInWithPassword: (username: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  hasPermission: (page: AdminPage) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [staffUser, setStaffUser] = useState<StaffUser | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastKnownRole, setLastKnownRole] = useState<StaffRole | null>(null);

  /**
   * Fetch staff user information from the API
   * Requirements: 2.4
   */
  const fetchStaffUser = useCallback(
    async (userId: string, accessToken: string) => {
      try {
        console.log('[AuthContext] Fetching staff user for Supabase ID:', userId);

        // Ensure API client has the token before making the request
        apiClient.setAuthToken(accessToken);

        const response = await apiClient.staffUsers.getBySupabaseId(userId);

        if (response.success && response.data) {
          console.log('[AuthContext] Staff user fetched:', response.data.role);

          // Check for role change - Requirements: 13.4
          if (lastKnownRole && lastKnownRole !== response.data.role) {
            console.log('[AuthContext] Role changed from', lastKnownRole, 'to', response.data.role);
            console.log('[AuthContext] Forcing re-authentication due to role change');

            // Force sign out to require re-authentication
            await supabase.auth.signOut();
            clearAuthData();
            setSession(null);
            setUser(null);
            setStaffUser(null);
            setLastKnownRole(null);
            apiClient.setAuthToken(null);
            return;
          }

          setStaffUser(response.data);
          setLastKnownRole(response.data.role);
        } else {
          console.error('[AuthContext] Staff user not found in response');
        }
      } catch (error) {
        console.error('[AuthContext] Error fetching staff user:', error);

        // Check if it's an authentication error
        if (error instanceof Error) {
          const errorMessage = error.message.toLowerCase();

          if (
            errorMessage.includes('401') ||
            errorMessage.includes('unauthorized') ||
            errorMessage.includes('no authorization token') ||
            errorMessage.includes('invalid token')
          ) {
            console.error('[AuthContext] Authentication error - token may be invalid or expired');
            console.error('[AuthContext] Please sign out and sign in again');

            // Optionally force sign out on auth errors
            // Uncomment the following lines to automatically sign out on auth errors:
            // await supabase.auth.signOut();
            // clearAuthData();
            // setSession(null);
            // setUser(null);
            // setStaffUser(null);
            // apiClient.setAuthToken(null);
          } else if (errorMessage.includes('404') || errorMessage.includes('not found')) {
            console.error('[AuthContext] Staff user not found in database');
            console.error('[AuthContext] This user may not have been set up as a staff member');
          } else {
            console.error('[AuthContext] Unexpected error:', errorMessage);
          }
        }
      }
    },
    [lastKnownRole]
  );

  /**
   * Check if the current user has permission to access a specific page
   * Requirements: 3.4, 6.3, 7.4, 11.1
   */
  const hasPermission = useCallback(
    (page: AdminPage): boolean => {
      if (!staffUser?.role) {
        console.log('[AuthContext] No staff user role, denying permission');
        return false;
      }

      const permissions = ROLE_PERMISSIONS[staffUser.role];
      const hasAccess = permissions?.includes(page) || false;

      console.log(
        `[AuthContext] Permission check for ${page}: ${hasAccess} (role: ${staffUser.role})`
      );
      return hasAccess;
    },
    [staffUser]
  );

  useEffect(() => {
    // Get initial session
    supabase.auth
      .getSession()
      .then(async ({ data: { session } }) => {
        try {
          console.log('[AuthContext] Initial session:', session ? 'Found' : 'None');
          console.log(
            '[AuthContext] Access token:',
            session?.access_token ? `${session.access_token.substring(0, 20)}...` : 'None'
          );

          // Check if session is expired - Requirements: 13.2
          if (session && isSessionExpired(session)) {
            console.log('[AuthContext] Session expired, signing out');
            await supabase.auth.signOut();
            clearAuthData();
            setSession(null);
            setUser(null);
            setStaffUser(null);
            apiClient.setAuthToken(null);
            setLoading(false);
            return;
          }

          setSession(session);
          setUser(session?.user ?? null);
          // Set auth token for API client
          apiClient.setAuthToken(session?.access_token ?? null);

          // Fetch staff user if authenticated
          if (session?.user && session.access_token) {
            await fetchStaffUser(session.user.id, session.access_token);
          }
        } catch (error) {
          console.error('[AuthContext] Error in initial session setup:', error);
          // Clear auth state on error
          setSession(null);
          setUser(null);
          setStaffUser(null);
          apiClient.setAuthToken(null);
        } finally {
          // Always set loading to false, even if there's an error
          setLoading(false);
        }
      })
      .catch((error) => {
        console.error('[AuthContext] Error getting initial session:', error);
        setLoading(false);
      });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      try {
        console.log('[AuthContext] Auth state changed:', event);
        console.log('[AuthContext] New session:', session ? 'Found' : 'None');

        // Handle session expiration - Requirements: 13.2
        if (event === 'TOKEN_REFRESHED') {
          console.log('[AuthContext] Token refreshed successfully');
        } else if (event === 'SIGNED_OUT') {
          console.log('[AuthContext] User signed out, clearing authentication data');
          // Clear all authentication data
          clearAuthData();
          setSession(null);
          setUser(null);
          setStaffUser(null);
          setLastKnownRole(null);
          apiClient.setAuthToken(null);
          setLoading(false);
          return;
        }

        setSession(session);
        setUser(session?.user ?? null);
        // Update auth token for API client whenever session changes
        apiClient.setAuthToken(session?.access_token ?? null);

        // Fetch staff user on auth state change
        if (session?.user && session.access_token) {
          await fetchStaffUser(session.user.id, session.access_token);
        } else {
          setStaffUser(null);
          setLastKnownRole(null);
        }
      } catch (error) {
        console.error('[AuthContext] Error in auth state change handler:', error);
        // Don't clear session on error in state change, just log it
      } finally {
        setLoading(false);
      }
    });

    // Periodic check for role changes - Requirements: 13.4
    // Check every 5 minutes if user is authenticated
    const roleCheckInterval = setInterval(
      async () => {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (session?.user && session.access_token) {
          console.log('[AuthContext] Periodic role check');
          await fetchStaffUser(session.user.id, session.access_token);
        }
      },
      5 * 60 * 1000
    ); // 5 minutes

    return () => {
      subscription.unsubscribe();
      clearInterval(roleCheckInterval);
    };
  }, [fetchStaffUser]);

  /**
   * Sign in with username and password
   * Requirements: 2.2
   */
  const signInWithPassword = async (username: string, password: string) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        return { error: new Error(result.message || 'Login failed') };
      }

      // Set the session manually
      const { data } = result;
      if (data?.session) {
        await supabase.auth.setSession({
          access_token: data.session.access_token,
          refresh_token: data.session.refresh_token,
        });
      }

      return { error: null };
    } catch (error) {
      console.error('[AuthContext] Login error:', error);
      return { error: error as Error };
    }
  };

  /**
   * Sign out and clear all authentication data
   * Requirements: 13.3
   */
  const signOut = async () => {
    try {
      // Invalidate session token with Supabase
      await supabase.auth.signOut();

      // Clear auth token from API client
      apiClient.setAuthToken(null);

      // Clear staff user
      setStaffUser(null);

      // Clear all authentication data from storage
      clearAuthData();

      console.log('[AuthContext] User signed out successfully');
    } catch (error) {
      console.error('[AuthContext] Error during sign out:', error);
      // Even if sign out fails, clear local state
      apiClient.setAuthToken(null);
      setStaffUser(null);
      clearAuthData();
    }
  };

  const value = {
    user,
    staffUser,
    session,
    loading,
    signInWithPassword,
    signOut,
    hasPermission,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
